<template>
  <button v-if="!link" :class="mode">
    <slot></slot>
  </button>
  <router-link v-else :to="to" :class="mode">
    <slot></slot>
  </router-link>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

/**
 * BaseButton - Button or Router Link component
 * 
 * Versatile button component that can render as either a <button> or <router-link>.
 * Supports multiple style modes: default, flat, and outline.
 * 
 * @example
 * <!-- Regular button -->
 * <base-button>Click Me</base-button>
 * 
 * <!-- Flat style -->
 * <base-button mode="flat">Flat Button</base-button>
 * 
 * <!-- As router link -->
 * <base-button link to="/dashboard">Go to Dashboard</base-button>
 */
export default defineComponent({
  name: 'BaseButton',
  props: {
    /**
     * Button style mode
     * @values null (default), 'flat', 'outline'
     */
    mode: {
      type: String as PropType<'flat' | 'outline' | null>,
      required: false,
      default: null
    },
    /**
     * Render as router-link instead of button
     */
    link: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Router link destination (only used when link=true)
     */
    to: {
      type: String,
      required: false,
      default: '/'
    }
  }
});
</script>

<style scoped>

button,
a {
  text-decoration: none;
  padding: var(--spacing-3) var(--spacing-6);
  font: inherit;
  font-weight: var(--font-weight-semibold);
  background-color: var(--color-primary);
  border: var(--border-width-thin) solid var(--color-primary);
  color: var(--color-text-inverse);
  cursor: pointer;
  border-radius: var(--radius-full);
  margin-right: var(--spacing-2);
  display: inline-block;
  transition: var(--transition-base);
}

a:hover,
a:active,
button:hover,
button:active {
  background-color: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-primary-md);
  transform: translateY(-1px);
}

button:active,
a:active {
  transform: translateY(0);
}

.flat {
  background-color: transparent;
  color: var(--color-primary);
  border: none;
}

.outline {
  background-color: transparent;
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.flat:hover,
.flat:active {
  background-color: var(--color-primary-alpha-10);
}

.outline:hover,
.outline:active {
  background-color: var(--color-primary-alpha-10);
  border-color: var(--color-primary-dark);
}
</style>